/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon } from '@/shared/ui/radix-icons';
import { VisitStatusBadge } from './VisitStatusBadge';
import type { KanbanVisit } from '@pages/authenticated/agency-pages/visits/useVisitsKanban';
import { Text } from '@/shared/ui';
import { useIsSoloBroker } from '@/shared/hooks/use-is-solo-broker';

interface VisitsMobileCardViewProps {
  visits: KanbanVisit[];
  selectedId: string | null;
  onCardClick: (id: string) => void;
}

/**
 * Mobile-friendly card representation of the visits table, mirroring the
 * Properties feature's {@link MobileCardView}. Each visit becomes a tappable
 * card with property, client, broker, schedule, and status.
 */
export const VisitsMobileCardView: React.FC<VisitsMobileCardViewProps> = ({ visits, selectedId, onCardClick }) => {
  const { t } = useTranslation('features/visits');
  const isSoloBroker = useIsSoloBroker();
  if (visits.length === 0) {
    return (
      <div style={{ padding: 'var(--space-8) 0', textAlign: 'center' }}>
        <CalendarIcon width={24} height={24} style={{ color: 'var(--text-tertiary)', opacity: 0.4 } as React.CSSProperties} />
        <Text style={{ fontSize: 'var(--text-body-sm)', color: 'var(--text-tertiary)', margin: 'var(--space-2) 0 0' }}>{t('kanban.noVisits')}</Text>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {visits.map((v) => {
        const selected = v.id === selectedId;
        return (
          <div
            key={v.id}
            data-testid="visit-card"
            onClick={() => onCardClick(v.id)}
            style={{
              background: selected ? 'color-mix(in srgb, var(--seed-base) 4%, var(--surface-primary))' : 'var(--surface-primary)',
              border: selected ? '1.5px solid var(--seed-base)' : '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--shadow-sm)',
              padding: 'var(--space-4)',
              cursor: 'pointer',
              transition: 'var(--transition-standard)',
              fontFamily: 'var(--font-family)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 'var(--space-2)' }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{
                    fontSize: 'var(--text-body-sm)',
                    fontWeight: 'var(--font-semibold)' as unknown as number,
                    color: 'var(--text-primary)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {v.client}
                </div>
                <div style={{ fontSize: 'var(--text-meta)', color: 'var(--text-secondary)', marginTop: 'var(--space-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {v.propertyShort}
                </div>
              </div>
              <VisitStatusBadge status={v.status} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--space-3)' }}>
              <Text as="span" style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)' }}>
                {v.date ? `${v.date.split('-').reverse().join('/')} ${v.time}` : '—'}
              </Text>
              {!isSoloBroker && v.broker && (
                <Text as="span" style={{ fontSize: 'var(--text-micro)', padding: 'var(--space-1) var(--space-2)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-tertiary)', color: 'var(--text-secondary)', fontWeight: 'var(--font-medium)' as unknown as number }}>
                  {v.broker}
                </Text>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
